import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * NoWifiAI 2026-08-29 (backlog 164) — THE CLAIM IS THE ACCOUNT.
 *
 * A random credential this browser mints once and keeps in localStorage,
 * the same shape the Android app mints for itself (Ownership.kt): 32 hex
 * characters from crypto.getRandomValues, never shown as a login. It is
 * presented on every request as `x-claim`; the intake service resolves what
 * it owns. No email, no password, no profile. If the browser's storage is
 * lost, the seven-word recovery key shown at training time rebinds the
 * Expert to a new claim (recover()).
 *
 * The old `oe_fingerprint` (Math.random, "fp_…") was the key of a parallel
 * ownership system that no longer exists. Nothing real was ever minted on
 * it, so it is simply ignored.
 */
/**
 * NoWifiAI 2026-09-25 (L28 [183, 245]) — THE APP HANDS ITS CLAIM TO THE SITE.
 *
 * The app opens nowifiai.com/#claim=<its claim>. Whatever follows # never
 * reaches a server (no access log, no CDN log, no Referer), so it is read
 * here in the browser and adopted in place of the browser's own claim. From
 * then on every job this browser commissions is filed under the PHONE's
 * identity, and the phone's launch sweep finds it and downloads it — the
 * person never sees a file. Adopting overwrites a browser-minted claim on
 * purpose: the phone is the thing that keeps the Experts. Runs once per
 * page load, before anything asks for the claim.
 */
export function adoptClaimFromUrl() {
  try {
    const h = window.location.hash || "";
    const m = /(?:^#|[#&])claim=([0-9a-fA-F]{16,64})(?:&|$)/.exec(h);
    if (!m) return null;
    const claim = m[1].toLowerCase();
    if (localStorage.getItem("experts_claim") !== claim) {
      localStorage.setItem("experts_claim", claim);
    }
    // Drop the fragment so it is not bookmarked, shared or re-read.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    return claim;
  } catch (_) {
    return null;
  }
}

export function getClaim() {
  let claim = localStorage.getItem("experts_claim");
  if (!claim || claim.length < 16) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    claim = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    localStorage.setItem("experts_claim", claim);
  }
  return claim;
}

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  config.headers["x-claim"] = getClaim();
  return config;
});

// ---- catalog (free Experts, served from R2)
export const fetchCatalog = (params = {}) => api.get("/catalog", { params }).then((r) => r.data);
export const fetchExpert = (slug) => api.get(`/catalog/${slug}`).then((r) => r.data);
export const fetchTags = () => api.get("/catalog/tags").then((r) => r.data);

// ---- the bar
export const validatePrompt = (prompt, turnstile_token) =>
  api.post("/screening/validate", { prompt, turnstile_token }).then((r) => r.data);
/** Free training. Returns {job_id, display_code, recovery_key, eta_seconds}. */
export const trainFree = (subject, engine_id) =>
  api.post("/jobs", { subject, engine_id }).then((r) => r.data);

/**
 * 2026-09-25 (L63): which Engine the bar trains for. Remembered per browser
 * so a person on Engine 4B is not asked again on every visit. Nothing is
 * sent anywhere by this; it only pre-selects the row in the forge panel.
 */
export function getPreferredEngine() {
  try { return localStorage.getItem("experts_engine") || ""; } catch (_) { return ""; }
}
export function setPreferredEngine(engine_id) {
  try { localStorage.setItem("experts_engine", engine_id || ""); } catch (_) { /* private mode */ }
}
/** 2026-10-26 (L190/L193): the training tiers at the web door — what each contains,
 *  its price, and when a run bought now would be ready. From the hub, relayed. */
export const fetchTiers = () => api.get("/tiers").then((r) => r.data);
/** Paid training (Stripe). Payload carries `scope` (standard · deep · deeper). */
export const startCheckout = (payload) => api.post("/checkout", payload).then((r) => r.data);
export const paymentStatus = (session_id) => api.get(`/payments/status/${session_id}`).then((r) => r.data);

// ---- a job, from the hub
export const fetchJob = (job_id) => api.get(`/jobs/${job_id}`).then((r) => r.data);
export const fetchDownload = (job_id) => api.get(`/jobs/${job_id}/download`).then((r) => r.data);

// ---- what this browser owns
export const fetchLibrary = () => api.get("/library").then((r) => r.data);
export const recover = (passphrase) => api.post("/recover", { passphrase }).then((r) => r.data);

// ---- app download page
export const fetchAppDownload = () => api.get("/app/download").then((r) => r.data);
export const fetchEngines = () => api.get("/engines").then((r) => r.data);
export const trackDownloadClick = (kind) =>
  api.post("/app/download-click", { kind }).then((r) => r.data).catch(() => null);

// ---- admin
export const fetchAdminStats = (token, days = 30) =>
  api.get("/admin/stats", { params: { days }, headers: { "x-admin-token": token } }).then((r) => r.data);
export const fetchAdminCatalog = (token) =>
  api.get("/admin/catalog", { headers: { "x-admin-token": token } }).then((r) => r.data);
export const toggleExpertPlaceholder = (token, slug, placeholder) =>
  api.post(`/admin/catalog/${slug}/toggle`, { placeholder },
    { headers: { "x-admin-token": token } }).then((r) => r.data);
